import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { normalizeText } from '../utils/dataUtils';
import { CHECKOUT_MOODS } from '../utils/daycareUtils';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';

export default function DaycareCheckout() {
    const { user, authorizedFetch } = useAuth();
    const navigate = useNavigate();
    const [visits, setVisits] = useState([]);
    const [visitId, setVisitId] = useState('');
    const [visitSearch, setVisitSearch] = useState('');
    const [checkoutMood, setCheckoutMood] = useState(null);
    const [checkoutBehaviorNotes, setCheckoutBehaviorNotes] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                // Fetch today's visits from the backend which now has better timezone handling
                const res = await authorizedFetch(`${API_URL}/api/daycare/visits/today`);
                const all = await res.json();
                // Filter only for those that haven't checked out yet
                setVisits(all.filter(v => !v.checkOutTime));
            } catch (err) {
                console.error(err);
                setError(err.message || 'Error al cargar las visitas');
            }
        })();
    }, [user, authorizedFetch]);

    const handleSubmit = async e => {
        e.preventDefault();
        setError('');
        if (!visitId) return setError('Select a visit to check out');
        try {
            const res = await authorizedFetch(
                `${API_URL}/api/daycare/visits/${visitId}`,
                {
                    method: 'PUT',
                    body: JSON.stringify({
                        checkOutTime: new Date().toISOString(),
                        ...(checkoutMood && { checkoutMood }),
                        ...(checkoutBehaviorNotes.trim() && { checkoutBehaviorNotes: checkoutBehaviorNotes.trim().slice(0, 1000) }),
                    })
                }
            );
            if (!res.ok) throw new Error(await res.text());
            navigate('/daycare/checkin/logs');
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to check out');
        }
    };

    return (
        <div className="max-w-md mx-auto p-4">
            <div className="bg-white p-6 rounded shadow">
                <h1 className="text-2xl font-semibold mb-4">Daily Check-Out</h1>
                <div className="mb-2">Date: {new Date().toLocaleDateString('es-ES')}</div>
                {error && <div className="text-red-600 mb-2">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block font-medium mb-1">Seleccionar visita</label>
                        <input
                            type="text"
                            className="dog-picker-search"
                            placeholder="Buscar perro..."
                            value={visitSearch}
                            onChange={e => setVisitSearch(e.target.value)}
                        />
                        <div className="dog-picker-list">
                            {visits
                                .filter(v => normalizeText(v.dog?.name).includes(normalizeText(visitSearch)))
                                .map(v => {
                                    const isSelected = visitId === v._id;
                                    const checkInTime = new Date(v.checkInTime).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                                    return (
                                        <button
                                            type="button"
                                            key={v._id}
                                            className={`dog-picker-card${isSelected ? ' dog-picker-card--selected' : ''}`}
                                            onClick={() => setVisitId(v._id)}
                                        >
                                            <div className="dog-picker-avatar">
                                                {v.dog?.pictureUrl || v.dog?.picture
                                                    ? <img src={v.dog.pictureUrl || v.dog.picture} alt={v.dog.name} />
                                                    : <span>{v.dog?.name?.[0]?.toUpperCase()}</span>
                                                }
                                            </div>
                                            <div className="dog-picker-info">
                                                <div className="dog-picker-name">{v.dog?.name}</div>
                                                <div className="dog-picker-breed">Entró a las {checkInTime}</div>
                                            </div>
                                            {isSelected && <span className="dog-picker-check">✓</span>}
                                        </button>
                                    );
                                })
                            }
                            {visits.filter(v => normalizeText(v.dog?.name).includes(normalizeText(visitSearch))).length === 0 && (
                                <div className="dog-picker-empty">No hay visitas activas</div>
                            )}
                        </div>
                    </div>

                    {/* Mood 3-face selector */}
                    <div>
                        <label className="block font-medium mb-1">¿Cómo se portó? <span className="text-gray-400 text-sm font-normal">(opcional)</span></label>
                        <div className="co-mood-grid">
                            {CHECKOUT_MOODS.map(m => {
                                const isSelected = checkoutMood === m.value;
                                return (
                                    <button key={m.value} type="button"
                                        className={`co-mood-card${isSelected ? ' co-mood-card--selected' : ''}`}
                                        style={{ background: isSelected ? m.bg : '#fff', borderColor: isSelected ? m.border : '#e5e7eb', color: m.color }}
                                        onClick={() => setCheckoutMood(isSelected ? null : m.value)}>
                                        <span className="co-mood-emoji">{m.emoji}</span>
                                        <span className="co-mood-label">{m.label}</span>
                                        <span className="co-mood-desc">{m.desc}</span>
                                        {isSelected && <span className="co-mood-check">✓</span>}
                                    </button>
                                );
                            })}
                        </div>
                        {checkoutMood && (
                            <div className="mt-3">
                                <label className="block font-medium mb-1 text-sm">Describe qué pasó</label>
                                <textarea rows={2} maxLength={1000}
                                    placeholder={checkoutMood === 'happy' ? 'Ej: Jugó todo el día, muy sociable…' : checkoutMood === 'neutral' ? 'Ej: Un poco inquieto al inicio…' : 'Ej: Ladridos frecuentes, no quiso comer…'}
                                    value={checkoutBehaviorNotes} onChange={e => setCheckoutBehaviorNotes(e.target.value)}
                                    className="w-full border rounded p-2 text-sm" />
                                <span className="text-xs text-gray-400 float-right">{checkoutBehaviorNotes.length}/1000</span>
                            </div>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-2 rounded"
                    >
                        Check Out
                    </button>
                </form>
            </div>
        </div>
    );
}
